import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ShipmentView, ShippingProviderOption } from '@matix/types';
import { shippingApi } from './api';

/**
 * The couriers this company has activated. Empty = the till hides shipping
 * entirely (the specification's "hide when nothing is configured"). Cached
 * for five minutes: activation is an admin act, not a per-order one.
 */
export function useShippingProviders() {
  return useQuery<ShippingProviderOption[]>({
    queryKey: ['shipping', 'providers'],
    queryFn: () => shippingApi.providers(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

/** Current shipment per order on a screen of cards — one request per poll. */
export function useShipmentSummaries(receiptIds: number[], enabled: boolean) {
  const key = receiptIds.slice().sort((a, b) => a - b).join(',');
  return useQuery<Record<number, ShipmentView>>({
    queryKey: ['shipping', 'summaries', key],
    queryFn: () => shippingApi.forOrders(receiptIds),
    enabled: enabled && receiptIds.length > 0,
    staleTime: 10 * 1000,
    refetchInterval: 20 * 1000,
  });
}

export function useShipmentForReceipt(receiptId: number | null) {
  return useQuery<{ current: ShipmentView | null; history: ShipmentView[] }>({
    queryKey: ['shipping', 'receipt', receiptId],
    queryFn: () => shippingApi.forReceipt(receiptId!),
    enabled: receiptId != null,
    staleTime: 5 * 1000,
  });
}

/** After any write, drop everything shipping-related so the cards catch up. */
export function useInvalidateShipping() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ['shipping'] });
}
